/**
 * useOrgScope — loads GET /scenario-os/admin/scope/me once and exposes
 * org/domain entitlements for coach UI pickers and nav.
 *
 * Response shape:
 *   {
 *     unrestricted: bool,   // true → domains may be empty meaning "all"
 *     role: string,
 *     org_ids: string[],
 *     domains: [{ domain_id, display_name, aliases? }],  // [] when unrestricted
 *     assignable_domains: [{ domain_id, display_name, aliases? }], // what a domain picker may offer
 *     canonical_domains: string[],
 *     ui_access: { ... }
 *   }
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../services/api';

export const AI_DOMAIN_ALIASES = ['ai', 'ai_beginner', 'ai_orchestrator', 'ai_entrepreneur'];

/** Expand AI canonical/legacy aliases into a Set of domain ids. */
export function expandAliases(domainIds) {
  const out = new Set(Array.isArray(domainIds) ? domainIds.filter(Boolean) : []);
  if ([...out].some((d) => AI_DOMAIN_ALIASES.includes(d))) {
    AI_DOMAIN_ALIASES.forEach((d) => out.add(d));
  }
  return out;
}

/**
 * Filter a domain catalog (or id list) by scope / authoring entitlements.
 * Returns all entries when unrestricted or isPlatformAdmin.
 */
export function filterDomainsByScope(catalog, {
  unrestricted = false,
  isPlatformAdmin = false,
  scopeDomains = [],
  authoringDomains = [],
} = {}) {
  const list = Array.isArray(catalog) ? catalog : [];
  if (isPlatformAdmin || unrestricted) return list;

  const fromScope = (scopeDomains || [])
    .map((d) => (typeof d === 'string' ? d : d?.domain_id))
    .filter(Boolean);
  const ids = fromScope.length ? fromScope : (authoringDomains || []);
  const allowed = expandAliases(ids);
  if (!allowed.size) return [];

  return list.filter((d) => {
    const id = typeof d === 'string' ? d : d?.domain_id || d?.id;
    return id && allowed.has(id);
  });
}

let _scopeCache = null;
let _scopePromise = null;

function fetchScopeOnce() {
  if (_scopeCache) return Promise.resolve(_scopeCache);
  if (_scopePromise) return _scopePromise;
  _scopePromise = adminAPI.getScope()
    .then(({ data }) => {
      _scopeCache = data || {};
      return _scopeCache;
    })
    .catch(() => {
      _scopeCache = null;
      return null;
    })
    .finally(() => { _scopePromise = null; });
  return _scopePromise;
}

/** Clear in-memory cache (e.g. after logout / role change). */
export function clearOrgScopeCache() {
  _scopeCache = null;
  _scopePromise = null;
}

export default function useOrgScope() {
  const [scope, setScope] = useState(_scopeCache);
  const [loading, setLoading] = useState(!_scopeCache);

  useEffect(() => {
    let mounted = true;
    if (_scopeCache) {
      setScope(_scopeCache);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    fetchScopeOnce().then((data) => {
      if (!mounted) return;
      setScope(data);
      setLoading(false);
    });
    return () => { mounted = false; };
  }, []);

  const unrestricted = !!scope?.unrestricted;
  const domains = useMemo(
    () => (Array.isArray(scope?.domains) ? scope.domains : []),
    [scope],
  );
  const assignableDomains = useMemo(
    () => (Array.isArray(scope?.assignable_domains) ? scope.assignable_domains : []),
    [scope],
  );
  const domainIds = useMemo(() => {
    if (domains.length) return domains.map((d) => d.domain_id).filter(Boolean);
    return Array.isArray(scope?.canonical_domains) ? scope.canonical_domains : [];
  }, [domains, scope]);
  const orgIds = useMemo(
    () => (Array.isArray(scope?.org_ids) ? scope.org_ids : []),
    [scope],
  );
  const canonicalDomainIds = useMemo(() => {
    if (Array.isArray(scope?.canonical_domains) && scope.canonical_domains.length) {
      return scope.canonical_domains;
    }
    return domainIds;
  }, [scope, domainIds]);

  const expandAliasesCb = useCallback((ids) => expandAliases(ids), []);

  return {
    unrestricted,
    domains,
    assignableDomains,
    domainIds,
    orgIds,
    canonicalDomainIds,
    loading,
    expandAliases: expandAliasesCb,
    uiAccess: scope?.ui_access || null,
    role: scope?.role || null,
    scope,
  };
}
